import type { BriefComment, NotePhase, TaskStatus } from '@dyspatch/core';

/**
 * Which phase a note written now on a task in each status belongs to, and null
 * where the task is over and the hub answers 409.
 *
 * Mirrored from core's `notePhaseForStatus` rather than imported, because
 * importing a *value* from `@dyspatch/core` pulls the barrel, and with it
 * `node:crypto`, into a browser bundle. Same trade as `event-kinds.ts` and
 * `briefs.ts`, safe for the same reason: `packages/hub/test/notes.test.ts`
 * pins this table to core's function for every task status.
 */
export const NOTE_PHASE_BY_STATUS: Record<TaskStatus, NotePhase | null> = {
  draft: 'plan',
  queued: 'plan',
  offered: 'plan',
  claimed: 'run',
  running: 'run',
  reporting: 'run',
  review: 'review',
  done: null,
  failed: null,
  cancelled: null,
};

export function notePhaseFor(status: TaskStatus): NotePhase | null {
  return NOTE_PHASE_BY_STATUS[status];
}

/** What the compose box promises for a note written at this phase, in a person's words. */
export function describeNoteEffect(phase: NotePhase): string {
  switch (phase) {
    case 'plan':
      return 'Folded into the plan at Go.';
    case 'run':
      return 'Delivered to the running agent within a heartbeat.';
    case 'review':
      return 'Sent back with the PR.';
  }
}

/** The phase, as the thread labels it beside each note. */
export function notePhaseLabel(phase: NotePhase): string {
  switch (phase) {
    case 'plan':
      return 'plan';
    case 'run':
      return 'while running';
    case 'review':
      return 'in review';
  }
}

/**
 * Findings nobody has done anything with, per task key, for the badge on a
 * graph node (docs/JOB-LIFECYCLE.md 2.8).
 *
 * "Unread" is not a thing the record holds: there is no read flag on a note,
 * and the one action a finding has is being filed as an issue. So the count is
 * the agent's findings on that task that carry no `issueUrl` yet, which is the
 * honest reading of "nobody has dealt with this". A person's note is never in
 * it, and neither is a finding on a task the brief no longer lists.
 *
 * Keyed by task key rather than task id because the graph is drawn on keys and
 * a schedule's template has no ids at all.
 */
export function unfiledFindingsByTaskKey(
  comments: readonly BriefComment[],
  tasks: readonly { id: string; key: string }[],
): Map<string, number> {
  const keyById = new Map(tasks.map((task) => [task.id, task.key]));
  const counts = new Map<string, number>();
  for (const comment of comments) {
    if (comment.authorKind !== 'agent' || comment.issueUrl || !comment.taskId) continue;
    const key = keyById.get(comment.taskId);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}
